import { registerAllNodeSchemas } from './register-all'
import {
  UniversalNodeRegistry,
  type CompoundDeclaration,
  type UniversalNodeSchema,
} from './universal-node-schema'
import type { Lowering } from '@/features/backtest/utils/graph-lowering'

/**
 * The lists read from the schemas' `compound` declarations (compound audit
 * step 3, 2026-10-06), where five files kept their own and disagreed: the
 * validator's placers left out the Trade Manager, the first-backtest
 * checklist's left out Entry Rules.
 *
 * Read lazily: registration is idempotent and runs first, so a caller outside
 * the editor (a test, the headless service) never sees an empty list. Each
 * list is rebuilt only when the registry changes, since some are read per node.
 */

type Singleton = NonNullable<CompoundDeclaration['singleton']>

let cachedAt = -1
let cached: {
  placers: ReadonlySet<string>
  editorOnly: ReadonlySet<string>
  excludedAi: ReadonlySet<string>
  excludedEdgescript: ReadonlySet<string>
  singletons: ReadonlyArray<{ type: string; singleton: Singleton }>
  runSettings: ReadonlySet<string>
  lowerings: ReadonlyArray<{ type: string; lower: Lowering }>
  entryWindowTriggers: ReadonlyMap<string, string>
} | null = null

function lists() {
  registerAllNodeSchemas()
  const count = UniversalNodeRegistry.getRegisteredCount()
  if (cached && cachedAt === count) return cached
  // Presets carry their base's declaration; the lists name base types only.
  const declared = UniversalNodeRegistry.getAllSchemas().filter(
    (s): s is UniversalNodeSchema & { compound: CompoundDeclaration } =>
      !!s.compound && !s.baseType,
  )
  const types = (keep: (c: CompoundDeclaration) => boolean) =>
    new Set(declared.filter((s) => keep(s.compound)).map((s) => s.type))
  cached = {
    placers: types((c) => c.placesTrades === true),
    editorOnly: types((c) => c.engine === 'editor-only'),
    excludedAi: types((c) => c.catalog?.ai === false),
    excludedEdgescript: types((c) => c.catalog?.edgescript === false),
    singletons: declared
      .filter((s) => s.compound.singleton)
      .map((s) => ({ type: s.type, singleton: s.compound.singleton! })),
    runSettings: new Set(declared.flatMap((s) => s.compound.runSettings ?? [])),
    lowerings: inRunOrder(declared),
    entryWindowTriggers: new Map(
      UniversalNodeRegistry.getAllSchemas()
        .filter((s) => !s.baseType && s.facets?.entryWindow)
        .map((s) => [s.type, s.facets!.entryWindow!.trigger]),
    ),
  }
  cachedAt = count
  return cached
}

/**
 * The declared lowerings, each after the ones it `runsAfter`. A cycle (or a
 * `runsAfter` naming a type without a lowering) throws: the run would
 * otherwise lower in an order nothing declared.
 */
function inRunOrder(
  declared: Array<UniversalNodeSchema & { compound: CompoundDeclaration }>,
): Array<{ type: string; lower: Lowering }> {
  const pending = new Map(
    declared
      .filter((s) => s.compound.lower)
      .map((s) => [s.type, { lower: s.compound.lower!, after: s.compound.runsAfter ?? [] }]),
  )
  const ordered: Array<{ type: string; lower: Lowering }> = []
  while (pending.size > 0) {
    const ready = [...pending].find(([, p]) => p.after.every((t) => !pending.has(t)))
    if (!ready) throw new Error(`Lowerings cannot be ordered: ${[...pending.keys()].join(', ')}`)
    const [type, { lower, after }] = ready
    const unknown = after.find((t) => !ordered.some((o) => o.type === t))
    if (unknown) throw new Error(`${type} runs after ${unknown}, which has no lowering`)
    ordered.push({ type, lower })
    pending.delete(type)
  }
  return ordered
}

/** Node types that place trades. A graph with none of them has nothing to run. */
export const tradePlacerTypes = (): ReadonlySet<string> => lists().placers

export const isTradePlacerType = (type: string | undefined): boolean =>
  type !== undefined && lists().placers.has(type)

/** Node types never sent to the engine: run settings and the sample, not logic. */
export const editorOnlyTypes = (): ReadonlySet<string> => lists().editorOnly

/** Node types a catalog leaves out. */
export const catalogExcludedTypes = (host: 'ai' | 'edgescript'): ReadonlySet<string> =>
  host === 'ai' ? lists().excludedAi : lists().excludedEdgescript

/** Node types a strategy has at most one of, with the readiness error a second raises. */
export const singletonSchemas = (): ReadonlyArray<{ type: string; singleton: Singleton }> =>
  lists().singletons

/** Settings that say how a run is launched, never how it trades. */
export const runSettingNames = (): ReadonlySet<string> => lists().runSettings

/** The compounds' lowerings, in the order the engine view runs them. */
export const compoundLowerings = (): ReadonlyArray<{ type: string; lower: Lowering }> =>
  lists().lowerings

/** Node types with the entry-window facet, to the socket each hears its trigger on. */
export const entryWindowTriggers = (): ReadonlyMap<string, string> => lists().entryWindowTriggers
